import React from "react";
import { Box, Typography } from "@mui/material";
import ReportSummaryCard, { summaryCardsRowSx } from "./ReportSummaryCard";
import { colors } from "../../theme/designSystem";
import type { CashBookSlice } from "../../services/accountsPayableService";
import type { CashScenarioPanel as CashScenarioPanelData } from "../../services/reportsService";

// Painel do Cenário de caixa (decisão do Hilton, 13/09/2026: saiu do Livro-caixa, que fica
// operacional). Três leituras: provisões em aberto a pagar/a receber, saldo em contas de hoje
// e os totais de atendimentos — os MESMOS números da Análise de Atendimentos.

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Bloco: total no topo e as linhas abaixo. Com 3 a 6 categorias, a tabela responde mais rápido que um donut. */
const SliceTable: React.FC<{
  titulo: string;
  linhas: CashBookSlice[];
  negativo?: boolean;
  saldo?: boolean;
  /** Posição de hoje (RN-208b): o subtítulo diz a data, não a quantidade de lançamentos. */
  posicao?: boolean;
}> = ({ titulo, linhas, negativo, saldo, posicao }) => {
  const total = linhas.reduce((s, l) => s + l.value, 0);
  const qtd = linhas.reduce((s, l) => s + l.count, 0);
  const cor = (v: number) => (saldo ? (v >= 0 ? "#28a745" : "#dc3545") : negativo ? "#dc3545" : "#28a745");

  return (
    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", background: "#fff" }}>
      <Box sx={{ px: 1.5, py: 1, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e2e8f0" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#495057" }}>{titulo}</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: cor(total), fontVariantNumeric: "tabular-nums" }}>
          {formatCurrency(total)}
          <Typography component="span" sx={{ fontSize: 11, color: "#adb5bd", ml: 0.75 }}>
            {posicao ? `posição de hoje, ${new Date().toLocaleDateString("pt-BR")}` : `${qtd} ${qtd === 1 ? "lançamento" : "lançamentos"}`}
          </Typography>
        </Typography>
      </Box>
      {linhas.length === 0 ? (
        <Typography sx={{ px: 1.5, py: 1.5, fontSize: 12, color: "#adb5bd" }}>{posicao ? "Nenhuma conta ativa." : "Nada no período."}</Typography>
      ) : (
        linhas.map((l) => (
          <Box
            key={l.label}
            sx={{ display: "flex", justifyContent: "space-between", gap: 1, px: 1.5, py: 0.75, borderBottom: "1px solid #f1f3f5" }}
          >
            <Typography sx={{ fontSize: 12, color: "#6c757d", minWidth: 0, overflowWrap: "anywhere" }}>
              {l.label}
              {!posicao && (
                <Typography component="span" sx={{ fontSize: 11, color: "#ced4da", ml: 0.5 }}>
                  ({l.count})
                </Typography>
              )}
            </Typography>
            <Typography sx={{ fontSize: 12, color: cor(l.value), fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {formatCurrency(l.value)}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
};

const CashScenarioPanel: React.FC<{ panel: CashScenarioPanelData; monthLabel: string }> = ({ panel, monthLabel }) => {
  const a = panel.attendances;
  // Mesmos rótulos e cores dos cards da Análise de Atendimentos.
  const cards = a
    ? [
        { label: "Agendamentos", value: String(a.totalSchedules) },
        { label: "Realizados", value: String(a.attended), color: "#32CD32" },
        { label: "Faltas cobráveis", value: String(a.chargeableNoShows), color: "#009900" },
        { label: "Cancelados", value: String(a.cancelled), color: "#FF6347" },
        { label: "Comparecimento", value: `${a.attendanceRate}%` },
        { label: "Valor realizado", value: formatCurrency(a.realizedValue), color: colors.primary },
        {
          label: "Valor pago",
          value: formatCurrency(a.paidValue),
          color: "#28a745",
          note: a.advancePaidValue >= 0.01 ? `+${formatCurrency(a.advancePaidValue)} antecipados` : undefined,
        },
        { label: "Valor pendente", value: formatCurrency(a.pendingValue), color: a.pendingValue >= 0.01 ? "#dc3545" : undefined },
        { label: "Repasse previsto", value: formatCurrency(a.professionalPayout) },
        a.taxRegime
          ? {
              label: "Impostos previstos",
              value: formatCurrency(a.taxForecast),
              sub: `${a.taxRegime} · ${a.taxPercent.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% sobre o valor realizado`,
            }
          : { label: "Impostos previstos", value: "—", sub: "sem regime tributário vigente", subColor: "#fd7e14" },
      ]
    : [];

  return (
    <Box sx={{ mb: 3 }}>
      {a && a.totalSchedules > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#495057", mb: 1 }}>
            Atendimentos de {monthLabel}
            <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#adb5bd", ml: 1 }}>
              agenda do mês, como na Análise de Atendimentos; o pendente entra em "Previsão — a receber", repasse e impostos em "Previsão — a pagar"; o pago já está no Livro-caixa
            </Typography>
          </Typography>
          <Box sx={summaryCardsRowSx}>
            {cards.map((card) => (
              <ReportSummaryCard key={card.label} {...card} />
            ))}
          </Box>
        </Box>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
        <SliceTable titulo={`Previsão — a pagar em ${monthLabel}`} linhas={panel.forecastPayable} negativo />
        <SliceTable titulo={`Previsão — a receber em ${monthLabel}`} linhas={panel.forecastReceivable} />
        <SliceTable titulo="Saldo em contas" linhas={panel.byAccount} saldo posicao />
      </Box>
    </Box>
  );
};

export default CashScenarioPanel;
